// Line rendering style

import log from '../../utils/log';
import {Style} from '../style';
import StyleParser from '../style_parser';
import gl from '../../gl/constants'; // web workers don't have access to GL context, so import all GL constants
import Texture from '../../gl/texture';
import VertexLayout from '../../gl/vertex_layout';
import {buildPolylines} from '../../builders/polylines';
import renderDashArray from './dasharray';
import Geo from '../../geo';
import WorkerBroker from '../../utils/worker_broker';
import hashString from '../../utils/hash';
import {shaderSrc_polygonsVertex, shaderSrc_polygonsFragment} from '../polygons/polygons';

export const Lines = Object.create(Style);

Lines.vertex_layouts = [[], []]; // first dimension is texcoords on/off, second is offsets on/off
Lines.variants = {}; // mesh variants by variant key
Lines.dash_textures = {}; // needs to be cleared on scene config update

const DASH_SCALE = 20; // adjustment factor for UV scale to for line dash patterns w/fractional pixel width

Object.assign(Lines, {
    name: 'lines',
    built_in: true,
    vertex_shader_src: shaderSrc_polygonsVertex,
    fragment_shader_src: shaderSrc_polygonsFragment,
    selection: true, // enable feature selection

    init() {
        Style.init.apply(this, arguments);

        // Tell the shader we want a order in vertex attributes, and to extrude lines
        this.defines.TANGRAM_LAYER_ORDER = true;
        this.defines.TANGRAM_EXTRUDE_LINES = true;
        this.defines.TANGRAM_TEXTURE_COORDS = true; // texcoords attribute is set to static when not needed

        // Additional single-allocated object used for holding outline style as it is processed
        // Separate from this.feature_style so that outline properties do not overwrite calculated
        // inline properties (outline call is made *within* the inline call)
        this.outline_feature_style = {};
        this.inline_feature_style = this.feature_style; // save reference to main computed style object
    },

    // Calculate width or offset at zoom given in `context`
    calcDistance (prop, context) {
        return (prop && StyleParser.evalCachedDistanceProperty(prop, context)) || 0;
    },

    // Calculate width or offset at next zoom (used for zoom-based interpolation in shader)
    calcDistanceNextZoom (prop, context) {
        context.zoom++;
        let val = this.calcDistance(prop, context);
        context.zoom--;
        return val;
    },

    // Calculate width at current and next zoom, and scaling factor between
    calcWidth (draw, style, context) {
        // line width in meters
        let width = this.calcDistance(draw.width, context);
        if (width < 0) {
            return; // skip lines with negative width
        }

        let next_width;
        if (draw.next_width) {
            next_width = this.calcDistanceNextZoom(draw.next_width, context);
        }
        else {
            next_width = width / 2; // when width is static, width at next zoom is just half as many tile units
        }

        if ((width === 0 && next_width === 0) || next_width < 0) {
            return false; // skip lines that don't interpolate to a positive value at next zoom
        }

        // these values are saved for later calculating the outline width, which needs to add the base line's width
        style.width_unscaled = width;
        style.next_width_unscaled = next_width;

        // calculate relative change in line width between zooms
        // interpolate from the line width at the zoom mid-point, towards/away from the previous/next integer zoom
        if (draw.next_width) {
            next_width *= 2; // NB: a given width is twice as big in screen space at the next zoom
            let mid_width = (width + next_width) * 0.5;
            style.width = mid_width * context.units_per_meter_overzoom; // width at zoom mid-point
            style.width_scale = 1 - (next_width / mid_width);
        }
        else {
            style.width = width * context.units_per_meter_overzoom;
            style.width_scale = 0;
        }

        // optional adjustment to texcoord width based on scale
        if (draw.texcoords) {
            // when drawing an outline, use the inline's texture scale
            // (e.g. keeps dashed outline pattern locked to inline pattern)
            if (draw.inline_texcoord_width) {
                style.texcoord_width = draw.inline_texcoord_width;
            }
            // when drawing an inline, calculate UVs based on line width
            else {
                // UVs can't calc for zero-width, use next zoom width in that case
                style.texcoord_width = (style.width_unscaled || style.next_width_unscaled) * context.units_per_meter_overzoom / context.tile.overzoom2; // shorten calcs
            }
        }

        return true;
    },

    // Calculate offset at current and next zoom, and scaling factor between
    calcOffset (draw, style, context) {
        // Pre-calculated offset passed
        // This happens when a line passes pre-computed offset values to its outline
        if (draw.offset_precalc) {
            style.offset = draw.offset_precalc;
            style.offset_scale = draw.offset_scale_precalc;
        }
        // Offset to calculate
        else if (draw.offset) {
            let offset = this.calcDistance(draw.offset, context);

            if (draw.next_offset) {
                let next_offset = this.calcDistanceNextZoom(draw.next_offset, context) * 2;

                if (Math.abs(offset) >= Math.abs(next_offset)) {
                    style.offset = offset * context.units_per_meter_overzoom;
                    if (offset !== 0) {
                        style.offset_scale = 1 - (next_offset / offset);
                    }
                    else {
                        style.offset_scale = 0;
                    }
                }
                else {
                    style.offset = next_offset * context.units_per_meter_overzoom;
                    if (next_offset !== 0) {
                        style.offset_scale = (1 - (offset / next_offset)) * -1;
                    }
                    else {
                        style.offset_scale = 0;
                    }
                }
            }
            else {
                style.offset = offset * context.units_per_meter_overzoom;
                style.offset_scale = 0;
            }
        }
        // No offset
        else {
            style.offset = 0;
            style.offset_scale = 0;
        }
    },

    _parseFeature (feature, draw, context) {
        var style = this.feature_style;

        // calculate line width at current and next zoom
        if (this.calcWidth(draw, style, context) === false) {
            return; // missing or zero width
        }

        // calculate line offset at current and next zoom
        this.calcOffset(draw, style, context);

        style.color = this.parseColor(draw.color, context);
        if (!style.color) {
            return;
        }

        style.variant = draw.variant; // pre-calculated mesh variant

        // height defaults to feature height, but extrude style can dynamically adjust height by returning a number or array (instead of a boolean)
        style.z = (draw.z && StyleParser.evalCachedDistanceProperty(draw.z || 0, context)) || StyleParser.defaults.z;
        style.height = feature.properties.height || StyleParser.defaults.height;
        style.extrude = StyleParser.evalProperty(draw.extrude, context);
        if (style.extrude) {
            if (typeof style.extrude === 'number') {
                style.height = style.extrude;
            }
            else if (Array.isArray(style.extrude)) {
                style.height = style.extrude[1];
            }
        }

        // Raise line height if extruded
        if (style.extrude && style.height) {
            style.z += style.height;
        }

        style.z *= Geo.height_scale;        // provide sub-meter precision of height values
        style.height *= Geo.height_scale;

        style.cap = draw.cap;
        style.join = draw.join;

        style.miter_limit = draw.miter_limit;
        style.tile_edges = draw.tile_edges; // usually activated for debugging, or rare visualization needs

        // Construct an outline style
        // Reusable outline style object, marked as already wrapped in cache objects (preprocessed = true)
        style.outline = style.outline || {
            width: {}, next_width: {},
            preprocessed: true
        };

        if (draw.outline && draw.outline.visible !== false && draw.outline.color && draw.outline.width) {
            // outline width in meters
            // NB: multiply by 2 because outline is applied on both sides of line
            let outline_width = this.calcDistance(draw.outline.width, context) * 2;
            let outline_next_width = this.calcDistanceNextZoom(draw.outline.next_width, context) * 2;

            if ((outline_width === 0 && outline_next_width === 0) || outline_width < 0 || outline_next_width < 0) {
                // skip lines that don't interpolate between zero or greater width
                style.outline.width.value = null;
                style.outline.next_width.value = null;
                style.outline.color = null;
                style.outline.inline_texcoord_width = null;
                style.outline.texcoords = false;
            }
            else {
                // Maintain consistent outline width around the line fill
                style.outline.width.value = outline_width + style.width_unscaled;
                style.outline.next_width.value = outline_next_width + style.next_width_unscaled;
                style.outline.inline_texcoord_width = style.texcoord_width;

                // Offset is directly copied from fill to outline, no need to re-calculate it
                style.outline.offset_precalc = style.offset;
                style.outline.offset_scale_precalc = style.offset_scale;

                // Inherited properties
                style.outline.color = draw.outline.color;
                style.outline.cap = draw.outline.cap;
                style.outline.join = draw.outline.join;
                style.outline.miter_limit = draw.outline.miter_limit;
                style.outline.texcoords = draw.outline.texcoords;
                style.outline.style = draw.outline.style;
                style.outline.variant = draw.outline.variant;

                // Explicitly defined outline order, or inherited from inner line
                if (draw.outline.order) {
                    style.outline.order = this.parseOrder(draw.outline.order, context);
                }
                else {
                    style.outline.order = style.order;
                }

                // Don't let outline be above inner line
                if (style.outline.order > style.order) {
                    style.outline.order = style.order;
                }

                // Outlines are always at half-layer intervals to avoid conflicting with inner lines
                style.outline.order -= 0.5;

                // Ensure outlines in a separate mesh variant are drawn first
                style.outline.variant_order = 0;
            }
        }
        else {
            style.outline.width.value = null;
            style.outline.next_width.value = null;
            style.outline.color = null;
            style.outline.inline_texcoord_width = null;
        }

        return style;
    },

    _preprocess (draw) {
        draw.color = StyleParser.createColorPropertyCache(draw.color);
        draw.width = StyleParser.createPropertyCache(draw.width, StyleParser.parseUnits);
        if (draw.width && draw.width.type !== StyleParser.CACHE_TYPE.STATIC) {
            draw.next_width = StyleParser.createPropertyCache(draw.width, StyleParser.parseUnits);
        }
        draw.offset = draw.offset && StyleParser.createPropertyCache(draw.offset, StyleParser.parseUnits);
        if (draw.offset && draw.offset.type !== StyleParser.CACHE_TYPE.STATIC) {
            draw.next_offset = StyleParser.createPropertyCache(draw.offset, StyleParser.parseUnits);
        }
        draw.z = StyleParser.createPropertyCache(draw.z, StyleParser.parseUnits);

        draw.dash = (draw.dash !== undefined ? draw.dash : this.dash);
        draw.dash_key = draw.dash && this.dashTextureKey(draw.dash);
        draw.dash_background_color = (draw.dash_background_color !== undefined ? draw.dash_background_color : this.dash_background_color);
        draw.dash_background_color = draw.dash_background_color && StyleParser.parseColor(draw.dash_background_color);
        draw.texture_merged = draw.dash_key || ((draw.texture !== undefined ? draw.texture : this.texture));
        draw.texcoords = ((this.texcoords || draw.texture_merged) ? 1 : 0);
        this.computeVariant(draw);

        if (draw.outline) {
            draw.outline.style = draw.outline.style || this.name;
            draw.outline.color = StyleParser.createColorPropertyCache(draw.outline.color);
            draw.outline.width = StyleParser.createPropertyCache(draw.outline.width, StyleParser.parseUnits);
            draw.outline.next_width = StyleParser.createPropertyCache(draw.outline.width, StyleParser.parseUnits); // width re-computed for next zoom

            draw.outline.cap = draw.outline.cap || draw.cap;
            draw.outline.join = draw.outline.join || draw.join;
            draw.outline.miter_limit = draw.outline.miter_limit || draw.miter_limit;
            draw.outline.offset = draw.offset;

            // outline inhertits dash pattern, but NOT explicit texture
            let outline_style = this.styles[draw.outline.style];
            if (outline_style) {
                draw.outline.dash = (draw.outline.dash !== undefined ? draw.outline.dash : outline_style.dash);
                draw.outline.texture = (draw.outline.texture !== undefined ? draw.outline.texture : outline_style.texture);

                if (draw.outline.dash != null) {            // dash was defined by outline draw or style
                    draw.outline.dash_key = draw.outline.dash && this.dashTextureKey(draw.outline.dash);
                    draw.outline.texture_merged = draw.outline.dash_key;
                }
                else if (draw.outline.dash === null) {      // dash explicitly disabled by outline draw or style
                    draw.outline.dash_key = null;
                    draw.outline.texture_merged = draw.outline.texture;
                }
                else if (draw.outline.texture != null) {    // texture was defined by outline draw or style
                    draw.outline.dash_key = null; // outline explicitly turning off dash
                    draw.outline.texture_merged = draw.outline.texture;
                }
                else {                                      // no dash or texture defined for outline, inherit parent dash
                    draw.outline.dash = draw.dash;
                    draw.outline.dash_key = draw.outline.dash && this.dashTextureKey(draw.outline.dash);
                    draw.outline.texture_merged = draw.outline.dash_key;
                }
                draw.outline.dash_background_color = (draw.outline.dash_background_color !== undefined ? draw.outline.dash_background_color : outline_style.dash_background_color);
                draw.outline.dash_background_color = (draw.outline.dash_background_color !== undefined ? draw.outline.dash_background_color : draw.dash_background_color);
                draw.outline.dash_background_color = draw.outline.dash_background_color && StyleParser.parseColor(draw.outline.dash_background_color);
                draw.outline.texcoords = ((outline_style.texcoords || draw.outline.texture_merged) ? 1 : 0);
                this.computeVariant(draw.outline);
            }
            else {
                log({ level: 'warn', once: true }, `Layer '${draw.layers[draw.layers.length-1]}': ` +
                    `line 'outline' specifies non-existent draw style '${draw.outline.style}' (or maybe the style is ` +
                    `defined but is missing a 'base' or has another error), skipping outlines in layer`);
                draw.outline = null;
            }
        }
        return draw;
    },

    // Unique string key for a dash pattern (used as texture name)
    dashTextureKey (dash) {
        return '__dash_' + JSON.stringify(dash);
    },

    // Return or render a dash pattern texture
    getDashTexture (dash) {
        let dash_key = this.dashTextureKey(dash);

        if (Lines.dash_textures[dash_key] == null) {
            Lines.dash_textures[dash_key] = true;

            // Render line pattern
            const dash_texture = renderDashArray(dash, { scale: DASH_SCALE });
            Texture.create(this.gl, dash_key, {
                data: dash_texture.pixels,
                height: dash_texture.length,
                width: 1,
                filtering: 'nearest'
            });
        }
    },

    // Override
    endData (tile) {
        return Style.endData.call(this, tile).then(tile_data => {
            if (tile_data) {
                tile_data.uniforms.u_has_line_texture = false;
                tile_data.uniforms.u_texture = Texture.default;
                tile_data.uniforms.u_v_scale_adjust = Geo.tile_scale;

                let pending = [];
                for (let m in tile_data.meshes) {
                    let variant = tile_data.meshes[m].variant;
                    if (variant.texture) {
                        let uniforms = tile_data.meshes[m].uniforms = tile_data.meshes[m].uniforms || {};
                        uniforms.u_has_line_texture = true;
                        uniforms.u_texture = variant.texture;
                        uniforms.u_texture_ratio = 1;

                        if (variant.dash) {
                            uniforms.u_v_scale_adjust = Geo.tile_scale * DASH_SCALE;
                            uniforms.u_dash_background_color = variant.dash_background_color || [0, 0, 0, 0];
                        }

                        if (variant.dash_key && Lines.dash_textures[variant.dash_key] == null) {
                            Lines.dash_textures[variant.dash_key] = true;
                            WorkerBroker.postMessage(this.main_thread_target+'.getDashTexture', variant.dash);
                        }

                        if (Texture.textures[variant.texture] == null) {
                            pending.push(
                                Texture.syncTexturesToWorker([variant.texture]).then(textures => {
                                    let texture = textures[variant.texture];
                                    if (texture) {
                                        uniforms.u_texture_ratio = texture.height / texture.width;
                                    }
                                })
                            );
                        }
                        else {
                            let texture = Texture.textures[variant.texture];
                            uniforms.u_texture_ratio = texture.height / texture.width;
                        }
                    }
                }
                return Promise.all(pending).then(() => tile_data);
            }
            return tile_data;
        });
    },

    // Calculate and store mesh variant (unique by draw group but not feature)
    computeVariant (draw) {
        let key = (draw.offset ? 1 : 0);
        if (draw.dash_key) {
            key += draw.dash_key;
            if (draw.dash_background_color) {
                key += draw.dash_background_color;
            }
        }

        if (draw.texture_merged) {
            key += draw.texture_merged;
        }
        key += '/' + draw.texcoords;
        key = hashString(key);
        draw.variant = key;

        if (Lines.variants[key] == null) {
            Lines.variants[key] = {
                key,
                order: draw.variant_order,
                offset: (draw.offset ? 1 : 0),
                texcoords: draw.texcoords,
                texture: draw.texture_merged,
                dash: draw.dash,
                dash_key: draw.dash_key,
                dash_background_color: draw.dash_background_color
            };
        }
    },

    // Override
    // Create or return desired vertex layout permutation based on flags
    vertexLayoutForMeshVariant (variant) {
        if (Lines.vertex_layouts[variant.key] == null) {
            // Basic attributes, others can be added (see texture UVs below)
            let attribs = [
                { name: 'a_position', size: 4, type: gl.SHORT, normalized: false },
                { name: 'a_extrude', size: 2, type: gl.SHORT, normalized: false },
                { name: 'a_offset', size: 2, type: gl.SHORT, normalized: false, static: (variant.offset ? null : [0, 0]) },
                { name: 'a_scaling', size: 2, type: gl.SHORT, normalized: false },
                { name: 'a_texcoord', size: 2, type: gl.UNSIGNED_SHORT, normalized: true, static: (variant.texcoords ? null : [0, 0]) },
                { name: 'a_color', size: 4, type: gl.UNSIGNED_BYTE, normalized: true },
                { name: 'a_selection_color', size: 4, type: gl.UNSIGNED_BYTE, normalized: true }
            ];
            Lines.vertex_layouts[variant.key] = new VertexLayout(attribs);
        }
        return Lines.vertex_layouts[variant.key];
    },

    // Override
    meshVariantTypeForDraw (draw) {
        return Lines.variants[draw.variant]; // return pre-calculated mesh variant
    },

    /**
     * A "template" that sets constant attibutes for each vertex, which is then modified per vertex or per feature.
     * A plain JS array matching the order of the vertex layout.
     */
    makeVertexTemplate(style, mesh) {
        let i = 0;

        // a_position.xyz - vertex position
        // a_position.w - layer order
        this.vertex_template[i++] = 0;
        this.vertex_template[i++] = 0;
        this.vertex_template[i++] = style.z || 0;
        this.vertex_template[i++] = this.scaleOrder(style.order);

        // a_extrude.xy - extrusion vector
        this.vertex_template[i++] = 0;
        this.vertex_template[i++] = 0;

        // a_offset.xy - normal vector
        // offset can be static or dynamic depending on style
        if (mesh.variant.offset) {
            this.vertex_template[i++] = 0;
            this.vertex_template[i++] = 0;
        }

        // a_scaling.xy - scaling to previous and next zoom
        this.vertex_template[i++] = style.width_scale * 1024;    // line width
        this.vertex_template[i++] = style.offset_scale * 1024;   // line offset

        // Add texture UVs to template only if needed
        if (mesh.variant.texcoords) {
            // a_texcoord.uv
            this.vertex_template[i++] = 0;
            this.vertex_template[i++] = 0;
        }

        // a_color.rgba
        this.vertex_template[i++] = style.color[0] * 255;
        this.vertex_template[i++] = style.color[1] * 255;
        this.vertex_template[i++] = style.color[2] * 255;
        this.vertex_template[i++] = style.color[3] * 255;

        // selection color
        if (this.selection) {
            // a_selection_color.rgba
            this.vertex_template[i++] = style.selection_color[0] * 255;
            this.vertex_template[i++] = style.selection_color[1] * 255;
            this.vertex_template[i++] = style.selection_color[2] * 255;
            this.vertex_template[i++] = style.selection_color[3] * 255;
        }

        return this.vertex_template;
    },

    buildLines(lines, style, mesh, context, options) {
        // Outline (build first so that blended geometry without a depth test is drawn first/under the inner line)
        this.feature_style = this.outline_feature_style; // swap in outline-specific style holder
        if (style.outline && style.outline.color != null && style.outline.width.value != null) {
            var outline_style = this.styles[style.outline.style];
            if (outline_style) {
                outline_style.addFeature(context.feature, style.outline, context);
            }
        }

        // Main line
        this.feature_style = this.inline_feature_style; // restore calculated style for inline
        let vertex_data = mesh.vertex_data;
        let vertex_layout = vertex_data.vertex_layout;
        let vertex_template = this.makeVertexTemplate(style, mesh);
        return buildPolylines(
            lines,
            style.width,
            vertex_data,
            vertex_template,
            {
                cap: style.cap,
                join: style.join,
                miter_limit: style.miter_limit,
                extrude_index: vertex_layout.index.a_extrude,
                offset_index: vertex_layout.index.a_offset,
                texcoord_index: vertex_layout.index.a_texcoord,
                texcoord_width: style.texcoord_width,
                texcoord_normalize: 65535, // scale UVs to unsigned shorts
                closed_polygon: options && options.closed_polygon,
                remove_tile_edges: !style.tile_edges && options && options.remove_tile_edges,
                tile_edge_tolerance: Geo.tile_scale * context.tile.pad_scale * 2,
                offset: style.offset
            }
        );
    },

    buildPolygons(polygons, style, mesh, context) {
         // Render polygons as individual lines
        let geom_count = 0;
         for (let p=0; p < polygons.length; p++) {
            geom_count += this.buildLines(polygons[p], style, mesh, context, { closed_polygon: true, remove_tile_edges: true });
         }
        return geom_count;
    }

});
